import client from './client';
import {
  AdminFormulation,
  AdminFormulationFull,
  AdminFormulationList,
  CartUsers,
  ConvertedOrders,
  FormulationUsers,
} from '../types/formulation';

const EMPTY_PAGE = { total: 0, page: 1, limit: 20, pages: 1 };

/** Everything generated in one cluster, newest first. `cluster` is the label. */
export async function getFormulations(params: {
  cluster?: string;
  page: number;
  limit?: number;
}): Promise<AdminFormulationList> {
  const res = await client.get('/api/admin/formulations', {
    params: { limit: 20, ...params },
  });
  const raw = res.data?.data ?? res.data ?? {};
  return {
    formulations: raw.formulations ?? [],
    pagination: raw.pagination ?? { ...EMPTY_PAGE, page: params.page },
  };
}

/** One formulation, for the detail panel. */
export async function getFormulation(id: string): Promise<AdminFormulation> {
  const res = await client.get(`/api/admin/formulations/${encodeURIComponent(id)}`);
  return (res.data?.data ?? res.data) as AdminFormulation;
}

/** Who has a formula sitting in their cart and has never bought. */
export async function getCartUsers(params: {
  page: number;
  limit?: number;
}): Promise<CartUsers> {
  const res = await client.get('/api/admin/analytics/cart-users', {
    params: { limit: 20, ...params },
  });
  const raw = res.data?.data ?? res.data ?? {};
  return {
    users: raw.users ?? [],
    pagination: raw.pagination ?? { ...EMPTY_PAGE, page: params.page },
  };
}

/* ─── Full input/output drill-downs ────────────────────────────────────────────
 * These three come back WITH the ml volumes. See the note in types/formulation.
 */

/** Every formula one user has generated, newest first. */
export async function getUserFormulations(
  userId: string,
  limit = 50,
): Promise<AdminFormulationFull[]> {
  const res = await client.get(
    `/api/admin/users/${encodeURIComponent(userId)}/formulations`,
    { params: { limit } },
  );
  const raw = res.data?.data ?? res.data ?? {};
  return raw.formulations ?? [];
}

/** Who generated a formula, and whether they went on to buy it. */
export async function getFormulationUsers(params: {
  page: number;
  limit?: number;
}): Promise<FormulationUsers> {
  const res = await client.get('/api/admin/analytics/formulation-users', {
    params: { limit: 50, ...params },
  });
  const raw = res.data?.data ?? res.data ?? {};
  return {
    users: raw.users ?? [],
    pagination: raw.pagination ?? { ...EMPTY_PAGE, page: params.page },
  };
}

/** The paid orders behind the funnel's last bar, each with its formula. */
export async function getConvertedOrders(params: {
  page: number;
  limit?: number;
}): Promise<ConvertedOrders> {
  const res = await client.get('/api/admin/analytics/converted-orders', {
    params: { limit: 50, ...params },
  });
  const raw = res.data?.data ?? res.data ?? {};
  return {
    orders: raw.orders ?? [],
    pagination: raw.pagination ?? { ...EMPTY_PAGE, page: params.page },
  };
}
